class StickySubToggle extends HTMLElement {
  constructor() {
    super();
    this.variantData = null;
    this.plans = null;
    this.currentVariantId = null;
    this.selectedPlanId = null;
    this.isSubscribed = false;
    this.observer = null;
    this.moneyFormatter = null;


    this._basePrice = 0;
    this._discountApplied = 0;
    this._subDiscount = 0;


    this._priceUpdatedHandler = null;
    this._purchaseOptionHandler = null;
    this._skioHandler = null;
    this._dropdownCloseHandler = null;
    this._resizeHandler = null;
    this._scrollHandler = null;
  }

  get hasPlans() {
    return this.dataset.hasPlans === 'true';
  }

  connectedCallback() {
    this._initFormatter();
    this._loadData();

    if (this.hasPlans) {
      this._populateDropdown();
      this._initFromPDPState(); 
    }

    this._setupObserver();
    this._setupListeners();
    this._updatePrices();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('price-updated', this._priceUpdatedHandler);
    document.removeEventListener('purchase-option::change', this._purchaseOptionHandler);
    document.removeEventListener('skio::update-selling-plan', this._skioHandler);
    document.removeEventListener('click', this._dropdownCloseHandler);
    window.removeEventListener('resize', this._resizeHandler);
    window.removeEventListener('scroll', this._scrollHandler);
  }

  _initFormatter() {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = window.Shopify?.locale || 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  _loadData() {

    const variantsEl = document.querySelector('#product-variants-data');
    if (variantsEl) {
      try { this.variantData = JSON.parse(variantsEl.textContent); } catch (_parseError) { /* noop */ }
    }

    const plansEl = document.querySelector('#sticky-sub-toggle-plans');
    if (plansEl) {
      try { this.plans = JSON.parse(plansEl.textContent); } catch (_parseError) { /* noop */ }
    }


    if (this.plans?.length) {
      this.selectedPlanId = (this.plans[1] ?? this.plans[0]).id;
      this._subDiscount = this.plans[0].discount || 0;
    }


    const mainSelect = document.querySelector('#product-select');
    this.currentVariantId = mainSelect?.value
      ? parseInt(mainSelect.value, 10)
      : this.variantData?.[0]?.id;

    
    if (this.variantData && this.currentVariantId) {
      const currentVariant = this.variantData.find(variantEntry => variantEntry.id == this.currentVariantId);
      if (currentVariant) this._basePrice = currentVariant.price;
    }

    
    const priceButtonEl = document.querySelector('price-button');
    this._discountApplied = parseFloat(priceButtonEl?.dataset.discountMetafield || 0);
  }

 
  _initFromPDPState() {  
    const activeFreqCard = document.querySelector('#purchase-form-logic .freq-card.active');
    if (activeFreqCard) {
      this.isSubscribed = activeFreqCard.dataset.type === 'subscribe';
      if (this.isSubscribed) {
        const activeDropdownItem = document.querySelector('#sellingPlanDropdown .dropdown-item.active');
        const activePlanId = activeDropdownItem?.getAttribute('data-selling-plan-id');
        if (activePlanId) this.selectedPlanId = parseInt(activePlanId, 10);
      }
    } else {    
      const skioPlanPicker = document.querySelector('skio-plan-picker');
      if (skioPlanPicker) {
        this.isSubscribed = !!skioPlanPicker.selectedSellingPlan;
        if (this.isSubscribed && skioPlanPicker.selectedSellingPlan?.id) {
          this.selectedPlanId = skioPlanPicker.selectedSellingPlan.id;
        }
      } else {
        this.isSubscribed = false;
      }
    }

    this._updateToggleUI();
  }

  _populateDropdown() {
    if (!this.plans?.length) return;
    const dropdownList = this.querySelector('.sst__dropdown-list');
    if (!dropdownList) return;

    dropdownList.innerHTML = this.plans
      .map((plan, planIndex) => `<li class="sst__dropdown-item${planIndex === 1 ? ' active' : ''}" data-plan-id="${plan.id}" role="option">${plan.name}</li>`)
      .join('');

    const defaultPlan = this.plans[1] ?? this.plans[0];
    const triggerText = this.querySelector('.sst__dropdown-selected-text');
    if (triggerText && defaultPlan) triggerText.textContent = defaultPlan.name;
  }


  _setupObserver() {
    const mainAtcButton = document.querySelector('button#add');
    if (!mainAtcButton) return;

    this.observer = new IntersectionObserver(
      ([intersectionEntry]) => {
        const atcIsBelow = intersectionEntry.boundingClientRect.top > 0;
        this._setVisible(!intersectionEntry.isIntersecting, atcIsBelow);
      },
      { threshold: 0.1 }
    );

    this.observer.observe(mainAtcButton);

    this._resizeHandler = () => this._updateTopOffset();
    this._scrollHandler = () => this._updateTopOffset();
    window.addEventListener('resize', this._resizeHandler, { passive: true });
    window.addEventListener('scroll', this._scrollHandler, { passive: true });
  }

  _updateTopOffset() {
    this.style.top = '';
  }

_setVisible(show, atcIsBelow = false) {
    if (show) this._updateTopOffset();
    this.classList.toggle('sst--visible', show);
    this.classList.toggle('sst--atc-below', show && atcIsBelow);
    document.body.classList.toggle('sticky-sub-toggle-open', show);
  }

  _setupListeners() {

    this._priceUpdatedHandler = ({ detail: priceDetail }) => {
      if (!priceDetail) return;
      if (priceDetail.variantId) this.currentVariantId = priceDetail.variantId;
      if (priceDetail.basePrice) {
        this._basePrice = priceDetail.basePrice;
        this._discountApplied = priceDetail.discountApplied || 0;
      }
      this._updatePrices();
    };
    document.addEventListener('price-updated', this._priceUpdatedHandler);

    this._purchaseOptionHandler = ({ detail: purchaseDetail }) => {
      if (!purchaseDetail || purchaseDetail.source !== 'pdp') return;
      this.isSubscribed = purchaseDetail.type === 'subscribe';
      if (this.isSubscribed && purchaseDetail.sellingPlanId) {
        this.selectedPlanId = parseInt(purchaseDetail.sellingPlanId, 10);
      }
      this._updateToggleUI();
      this._updatePrices();
    };
    document.addEventListener('purchase-option::change', this._purchaseOptionHandler);

    this._skioHandler = ({ detail: skioDetail }) => {
      if (!skioDetail) return;
      const incomingSubscribed = !!skioDetail.sellingPlan;
      const incomingPlanId = skioDetail.sellingPlan?.id ?? null;

      const alreadySynced =
        this.isSubscribed === incomingSubscribed &&
        (!incomingSubscribed || String(this.selectedPlanId) === String(incomingPlanId));

      if (alreadySynced) return;

      this.isSubscribed = incomingSubscribed;
      if (this.isSubscribed && incomingPlanId) this.selectedPlanId = incomingPlanId;
      this._updateToggleUI();
      this._updatePrices();
    };
    document.addEventListener('skio::update-selling-plan', this._skioHandler);


    if (this.hasPlans) {
      this.querySelector('.sst__toggle')?.addEventListener('click', () => this._handleToggle());

      this.querySelector('.sst__dropdown-trigger')?.addEventListener('click', (clickEvent) => {
        clickEvent.stopPropagation();
        const dropdownEl = this.querySelector('.sst__dropdown');
        const isOpen = dropdownEl?.classList.toggle('sst__dropdown--open');
        this.querySelector('.sst__dropdown-trigger')?.setAttribute('aria-expanded', String(!!isOpen));
      });


      this.querySelector('.sst__dropdown-list')?.addEventListener('click', (clickEvent) => {
        const selectedItem = clickEvent.target.closest('.sst__dropdown-item');
        if (!selectedItem) return;
        clickEvent.stopPropagation();

        this.querySelectorAll('.sst__dropdown-item').forEach(item => item.classList.remove('active'));
        selectedItem.classList.add('active');

        const triggerText = this.querySelector('.sst__dropdown-selected-text');
        if (triggerText) triggerText.textContent = selectedItem.textContent.trim();

        this.querySelector('.sst__dropdown')?.classList.remove('sst__dropdown--open');
        this.querySelector('.sst__dropdown-trigger')?.setAttribute('aria-expanded', 'false');

        this.selectedPlanId = parseInt(selectedItem.dataset.planId, 10);
        this._updatePrices();
        this._dispatchChange();
      });

      // Close dropdown when clicking outside
      this._dropdownCloseHandler = () => {
        this.querySelector('.sst__dropdown')?.classList.remove('sst__dropdown--open');
        this.querySelector('.sst__dropdown-trigger')?.setAttribute('aria-expanded', 'false');
      };
      document.addEventListener('click', this._dropdownCloseHandler);
    }

    this.querySelector('.sst__atc-btn')?.addEventListener('click', () => this._handleATC());
  }

  _handleToggle() {
    this.isSubscribed = !this.isSubscribed;
    this._updateToggleUI();
    this._updatePrices();
    this._dispatchChange();
  }

  _dispatchChange() {

    document.dispatchEvent(new CustomEvent('purchase-option::change', {
      detail: {
        type: this.isSubscribed ? 'subscribe' : 'onetime',
        sellingPlanId: this.isSubscribed ? this.selectedPlanId : null,
        source: 'sticky',
      },
    }));

    const skioPlanPicker = document.querySelector('skio-plan-picker');
    if (!skioPlanPicker) return;

    if (this.isSubscribed && this.selectedPlanId) {
      skioPlanPicker.selectSellingPlan?.(this.selectedPlanId);
    } else {
      skioPlanPicker.selectSellingPlanGroup?.(null);
    }
  }

  _updateToggleUI() {
    const toggleBtn = this.querySelector('.sst__toggle');
    const toggleLabel = this.querySelector('.sst__toggle-label');
    const freqWrap = this.querySelector('.sst__freq-wrap');

    toggleBtn?.setAttribute('aria-pressed', String(this.isSubscribed));
    this.classList.toggle('sst--subscribed', this.isSubscribed);

    if (toggleLabel) {
      const pct = Number(this._subDiscount) || 0;
      const hasDiscount = pct > 0;

      const template = this.isSubscribed
        ? (hasDiscount ? this.dataset.textSubscribed : this.dataset.textSubscribedZero)
        : (hasDiscount ? this.dataset.textUnsubscribed : this.dataset.textUnsubscribedZero);

      toggleLabel.textContent = (template || '').replace('{percent}', pct);
    }

    if (freqWrap) {
      this.isSubscribed ? freqWrap.removeAttribute('hidden') : freqWrap.setAttribute('hidden', '');
    }

    if (this.selectedPlanId) {
      const activeItem = this.querySelector(`.sst__dropdown-item[data-plan-id="${this.selectedPlanId}"]`);
      if (activeItem) {
        this.querySelectorAll('.sst__dropdown-item').forEach(item => item.classList.remove('active'));
        activeItem.classList.add('active');
        const triggerText = this.querySelector('.sst__dropdown-selected-text');
        if (triggerText) triggerText.textContent = activeItem.textContent.trim();
      }
    }
  }

  _updatePrices() {
    const priceEl = this.querySelector('.sst__atc-price');
    const compareEl = this.querySelector('.sst__atc-compare');

    let displayPrice;
    let comparePrice = null;

    if (this.isSubscribed && this.selectedPlanId && this.variantData) {

      const currentVariant = this.variantData.find((variantEntry) => variantEntry.id == this.currentVariantId);
      const planAllocation = currentVariant?.selling_plan_allocations?.find(
        (allocationEntry) => allocationEntry.selling_plan_id == this.selectedPlanId
      );
      displayPrice = planAllocation?.price ?? Math.round(this._basePrice * 0.85);
      comparePrice = displayPrice < this._basePrice ? this._basePrice : null;
    } else {
      const currentVariant = this.variantData?.find(v => v.id == this.currentVariantId);
      const compareAtPrice = currentVariant?.compare_at_price;

      displayPrice = this._discountApplied > 0
        ? Math.round(this._basePrice * (1 - this._discountApplied / 100))
        : this._basePrice;

      if (compareAtPrice && compareAtPrice > displayPrice) {
        comparePrice = compareAtPrice;
      } else if (this._discountApplied > 0) {
        comparePrice = this._basePrice;
      }
    }

    if (priceEl) priceEl.textContent = ` - ${this._formatMoney(displayPrice)}`;

    if (compareEl) {
      if (comparePrice !== null) {
        compareEl.textContent = this._formatMoney(comparePrice);
        compareEl.removeAttribute('hidden');
      } else {
        compareEl.setAttribute('hidden', '');
      }
    }
  }

  _formatMoney(cents) {
    return this.moneyFormatter.format(Math.floor(cents / 100));
  }

  _handleATC() {
    const mainAtcButton = document.getElementById('add');
    const atcForm = document.getElementById('add-to-cart');

    if (!mainAtcButton) return;

    if (!this.hasPlans || !atcForm) {
      mainAtcButton.click();
      return;
    }

    let planInput = atcForm.querySelector('input[name="selling_plan"]');
    let inputWasCreated = false;

    if (!planInput) {

      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      atcForm.appendChild(planInput);
      inputWasCreated = true;
    }

    const previousPlanValue = planInput.value;

    planInput.value = (this.isSubscribed && this.selectedPlanId)
      ? String(this.selectedPlanId)
      : '';

    mainAtcButton.click();

    if (inputWasCreated) {
      atcForm.removeChild(planInput);
    } else {
      planInput.value = previousPlanValue;
    }
  }
}

customElements.define('sticky-sub-toggle', StickySubToggle);
